import classNames from 'classnames';
import get from 'lodash/get';
import React, { memo, useMemo, useRef } from 'react';
import Highlighter from 'react-highlight-words';
import { DivTooltip } from '@wg/wows-react-uikit';

import { highlighterPercentRegexp } from '~/settings';
import { t } from '~/helpers/localization';

import LevelTooltip from '~/Components/ClanSupply/LevelTooltip/LevelTooltip';

import styles from './Building.scss';

import BuildingLevel from './BuildingLevel';
import BuildingIcon from '../BuildingIcon/BuildingIcon';

import type { BUILDINGS } from '~/Actions/ActionSupply';
import type { RegularReward, RegularRewardsType } from '~/Reducers/ReducerTreasury';
import type { ISupplyBuildingLevel } from '~/store/slices/settingsSlice';
import type { IBattlesStats } from '~/types/declaration';
import type { Nullable } from '~/types/generics';

type IProps = {
  accumulativeResource: number;
  balance: number;
  battlesStats: IBattlesStats;
  hasBuildPermission: boolean;
  isBuildingInProgress: boolean;
  isConfirmVisible: boolean;
  isOwnBase: boolean;
  isSelected: boolean;
  isUpgradeDisabled: boolean;
  level: number;
  levels: Record<number, ISupplyBuildingLevel>;
  mapHost: string;
  maxLevel: number;
  name: BUILDINGS;
  regularRewards: RegularRewardsType;
  onLevelClick: () => void;
  onMouseOverLevel: (meta: ISupplyBuildingLevel['meta'], level: number) => void;
  onMouseOutLevel: () => void;
  navigateToTreasury: () => void;
};

const Building: React.FC<IProps> = (props) => {
  const timeouts = useRef<Nullable<ReturnType<typeof setTimeout>>>(null);

  const mouseEnter = (meta: ISupplyBuildingLevel['meta'], level: number) => () => {
    if (timeouts.current) {
      clearTimeout(timeouts.current);
    }

    timeouts.current = setTimeout(() => {
      props.onMouseOverLevel(meta, level);
      if (timeouts.current) {
        clearTimeout(timeouts.current);
        timeouts.current = null;
      }
    }, 500);
  };

  const mouseLeave = () => () => {
    if (timeouts.current) {
      clearTimeout(timeouts.current);
      timeouts.current = null;
    }
    setTimeout(() => {
      if (!timeouts.current) {
        props.onMouseOutLevel();
      }
    }, 500);
  };

  const { levelsParams, isVisibleUpgradeIcon } = useMemo(() => {
    const params: Record<
      number,
      {
        isBuilt: boolean;
        isAvailable: boolean;
        isLevelBuildingInProgress: boolean;
        isRequiredPreviousLevel: boolean;
      }
    > = {};
    let visibleUpgradeIcon = false;

    Object.keys(props.levels).forEach((key) => {
      const index = Number(key);
      if (index === 0) {
        return;
      }

      const levelItem = props.levels[index];
      const isBuilt = index <= props.level;
      const hasEnoughResource = levelItem.meta && props.balance >= levelItem.meta.cost;
      const isRequiredPreviousLevel = index > props.level + 1;
      const hasEnoughAccumulativeResource =
        levelItem.meta && props.accumulativeResource >= (levelItem?.meta.accumulativeResourceRequired || 0);
      const isAvailable =
        hasEnoughResource &&
        hasEnoughAccumulativeResource &&
        !isBuilt &&
        !isRequiredPreviousLevel &&
        props.hasBuildPermission;
      const isNextLevel = index === props.level + 1;
      const isLevelBuildingInProgress = props.isBuildingInProgress && isNextLevel;

      params[index] = {
        isBuilt,
        isAvailable,
        isLevelBuildingInProgress,
        isRequiredPreviousLevel,
      };

      if (isAvailable) {
        visibleUpgradeIcon = true;
      }
    });

    return { levelsParams: params, isVisibleUpgradeIcon: visibleUpgradeIcon };
  }, [
    props.levels,
    props.level,
    props.balance,
    props.accumulativeResource,
    props.hasBuildPermission,
    props.isBuildingInProgress,
  ]);

  const renderLevels = () => {
    return Object.keys(props.levels).map((key) => {
      const index = Number(key);
      if (index === 0) {
        return null;
      }

      const levelItem = props.levels[index];
      const params = levelsParams[index];
      const accumulativeResourceRequired = props.accumulativeResource < levelItem.meta.accumulativeResourceRequired;
      const buyable = get(levelItem, 'meta.buyable', false);

      return (
        <DivTooltip
          key={`level-tooltip-${index}`}
          tooltipBody={
            <LevelTooltip
              meta={levelItem.meta}
              level={index}
              name={props.name}
              title={levelItem.title}
              price={levelItem.meta.cost}
              bonus={
                levelItem.meta.bonus ? (
                  <Highlighter
                    highlightClassName="HighlightPercent"
                    searchWords={highlighterPercentRegexp}
                    textToHighlight={levelItem.meta.bonus}
                  />
                ) : null
              }
              balance={props.balance}
              accumulativeResource={props.accumulativeResource}
              isAvailable={params.isAvailable}
              isReady={params.isBuilt}
              hasBuildPermission={props.hasBuildPermission}
              isRequiredPreviousLevel={params.isRequiredPreviousLevel}
              isOwnBase={props.isOwnBase}
              battlesStats={props.battlesStats}
            />
          }
        >
          <BuildingLevel
            isAvailable={params.isAvailable && !accumulativeResourceRequired && buyable}
            isBuildingInProgress={params.isLevelBuildingInProgress}
            isBuilt={params.isBuilt}
            isConfirmVisible={props.isConfirmVisible}
            isDisabled={props.isUpgradeDisabled}
            isRegularIcon={buyable}
            onClick={params.isAvailable ? props.onLevelClick : () => {}}
            onMouseEnter={mouseEnter(levelItem.meta, index)}
            onMouseLeave={mouseLeave()}
          />
        </DivTooltip>
      );
    });
  };

  const renderTreasuryRewardStatus = (reward: RegularReward) => {
    const isAvailable = props.balance >= reward.price && props.level >= reward.requiredLevel;

    if (isAvailable) {
      return (
        <span onClick={props.navigateToTreasury} className={styles.rewardStatusAvailable}>
          {t('доступно')}
        </span>
      );
    }

    return <span className={styles.rewardStatus}>{t('недоступно')}</span>;
  };

  const renderTreasuryBlock = () => {
    return (
      <div className={styles.treasuryBlock}>
        <div className={styles.rewards}>
          {props.regularRewards.availableRewardOffers
            .filter((reward) => reward.interval)
            .map((reward, i) => (
              <div className={styles.reward} key={`reward-${i}`}>
                {reward.title}
                {renderTreasuryRewardStatus(reward)}
              </div>
            ))}
        </div>
      </div>
    );
  };

  const data = props.levels[props.level] || null;
  if (!data?.meta) {
    return null;
  }

  const notBuilt = props.level === 0;
  const isTreasury = props.name === 'treasury';
  const buyable = get(data, 'meta.buyable', false);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <BuildingIcon className={classNames(styles.icon, notBuilt && styles.notBuilt)} buildingId={props.name} />
        <div>
          <span className={styles.title}>{data.title}</span>
          <div className={styles.buildInfo}>
            <p>
              {t('Улучшений: %(level)s из %(maxLevel)s', {
                level: props.level,
                maxLevel: props.maxLevel,
              })}
            </p>
            {isVisibleUpgradeIcon && buyable && props.isOwnBase && <span className={styles.availableUpgradeIcon} />}
          </div>
        </div>
      </div>
      {notBuilt && <div className={styles.description}>{data.meta.function}</div>}
      <div className={styles.bonus}>
        {notBuilt ? (
          t('Здание не построено')
        ) : (
          <Highlighter
            highlightClassName="HighlightPercent"
            searchWords={highlighterPercentRegexp}
            textToHighlight={get(data, 'meta.totalBonus', '')}
          />
        )}
      </div>
      {isTreasury && renderTreasuryBlock()}
      {props.isSelected && (
        <div className={classNames(styles.levelsWrapper, styles.isShow)}>
          <div className={styles.levels}>{renderLevels()}</div>
        </div>
      )}
    </div>
  );
};

export default memo(Building);
